/**
 * 特典・キャンペーン一覧画面
 * 契約者向けキャンペーン情報と、お友達紹介クーポン導線を表示する。
 * 初回表示時にキャンペーン情報APIを呼び出し、契約プランやキャンペーン管理方式に応じて表示内容を切り替える。
 */
import Heading from '@/components/atoms/Heading';
import ScrollLinkList from '@/components/molecules/ScrollLinkList';
import MenuPresentCampaign from '@/components/organisms/campaign/MenuPresentCampaign';
import CampaignInfo from '@/components/organisms/CampaignInfo';
import IntroducerRewards from '@/components/organisms/IntroducerRewards';
import { AnalyticsData } from '@/components/templates/AnalyticsData';
import BreadcrumbLists from '@/components/templates/BreadcrumbLists';
import useCallAPIGetCampaignInfo from '@/hooks/api/useCallAPIGetCampaignInfo';
import { CampaignInfoState } from '@/states/campaignInfoState';
import { userState, UserStateType } from '@/states/selectors/userState';
import { NextPage } from 'next';
import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';

const Campaign: NextPage = () => {
  /** ユーザー情報 */
  const user = useRecoilValue(userState);

  /** キャンペーン情報照会State */
  const campaignInfo = useRecoilValue(CampaignInfoState);

  /** キャンペーン情報照会API */
  const { execute: callAPIGetCampaignInfo } = useCallAPIGetCampaignInfo();

  /**
   * 初期表示。
   */
  useEffect(() => {
    callAPIGetCampaignInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 「お友達紹介クーポン」の表示有無を取得する。
   * @param _user ユーザ情報
   * @param _is_customer_campaign キャンペーン判定システム
   * @returns 表示する場合は、trueを返却する。それ以外は、falseを返却する。
   */
  const isShowIntroducerRewards = (
    _user: UserStateType,
    _is_customer_campaign: boolean
  ): boolean => {
    // キャンペーン管理システムを使用しているプランのみ表示する。
    if (campaignInfo.is_customer_campaign) {
      return false;
    }
    if (campaignInfo?.campaigns?.length >= 1) {
      return true;
    }
    if (campaignInfo?.cashback_list?.length >= 1) {
      return true;
    }
    // 「ハイエンド」の場合は、キャンペーン管理システムの参照先により表示を切り替える。
    if (_user.isHighend) {
      if (_is_customer_campaign === false) {
        return true;
      }
    }
    return false;
  };

  /** リンクリスト */
  const linkList = (() => {
    const items = [{ anchor: 'campaignInfo', title: '特典・キャンペーン情報' }];
    if (isShowIntroducerRewards(user, campaignInfo.is_customer_campaign)) {
      items.push({ anchor: 'introducerRewards', title: 'お友達紹介クーポン' });
    }
    return items;
  })();

  /**
   * 「お友達紹介クーポン」の表示コンポーネント。
   * @returns 表示情報
   */
  const jsxIntroducerRewards = (): React.JSX.Element | null => {
    if (isShowIntroducerRewards(user, campaignInfo.is_customer_campaign) === false) {
      return null;
    }
    return <IntroducerRewards anchor='introducerRewards' />;
  };

  return (
    <>
      <AnalyticsData />
      <main className='l-main'>
        <Heading as='h1'>特典・キャンペーン</Heading>
        <ScrollLinkList items={linkList} />
        <CampaignInfo anchor='campaignInfo' />
        <div>{jsxIntroducerRewards()}</div>
        <MenuPresentCampaign />
        <BreadcrumbLists />
      </main>
    </>
  );
};

export default Campaign;
